import {format} from 'date-fns';
import {ymdFromSessionTypeDate} from './bookingSessionTypeWindow';

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True only for a real YYYY-MM-DD. Impossible days (10/32, 02/30) are false. */
export function isRealCalendarDate(value: string): boolean {
  const match = CALENDAR_DATE.exec(value.trim());
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
}

/**
 * Display/edit text for a session-type DATE.
 * Uses the stored calendar day, including when node-pg serialized local
 * midnight as a UTC instant (OD-1356). Never formats that instant in the
 * viewer timezone, which moves the day back west of UTC.
 */
export function formatSessionTypeCalendarDate(
  value: unknown,
  pattern: string,
): string {
  const ymd = ymdFromSessionTypeDate(value);
  if (!isRealCalendarDate(ymd)) return '';
  const [year, month, day] = ymd.split('-').map(Number);
  return format(new Date(year, month - 1, day), pattern);
}

export function sessionTypeDateFieldError(
  label: string,
  raw: string | undefined | null,
): string | undefined {
  const value = raw?.trim() ?? '';
  if (!value) return `${label} is required`;
  if (!isRealCalendarDate(value)) return `${label} is not a valid date`;
  return undefined;
}

export function sessionTypeDateRangeError(
  startDate: string,
  endDate: string,
): string | undefined {
  if (endDate < startDate) return 'End date must be greater than start date';
  return undefined;
}

/** Message when the native date input is holding an impossible day such as 10/32. */
export function invalidTypedDateMessage(
  startInvalid: boolean,
  endInvalid: boolean,
): string | undefined {
  if (startInvalid) return 'Start date is not a valid date';
  if (endInvalid) return 'End date is not a valid date';
  return undefined;
}

export function markCalendarDateValidity(
  badInput: boolean,
  invalidRef: {current: boolean},
): void {
  invalidRef.current = badInput;
}

/**
 * Keep the previous stored day when the browser reports an impossible date.
 * Writing that day back is what made 10/32 save as the previous valid date.
 */
function isImpossibleTypedDate(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== '' && !isRealCalendarDate(trimmed);
}

export function commitCalendarDateChange(
  badInput: boolean,
  value: string,
  invalidRef: {current: boolean},
  setValue: (next: string) => void,
): void {
  if (badInput || isImpossibleTypedDate(value)) {
    invalidRef.current = true;
    return;
  }
  invalidRef.current = false;
  setValue(value);
}
